/**
 * Lets the DICOM import dialog act on series the clinician accepted in the
 * agent chat. It drives the dialog ONLY through the dialog's own handlers
 * (select patient → tick series), one step per render, then clears the
 * request. The clinician still presses Load, so the dialog's download and
 * patient-conflict checks run unchanged. With no request (the manual flow)
 * it does nothing.
 */
import { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import type { DICOMPatientPresentation } from '~/backend/connector-api/connector-schemas';
import { agentDicomImportRequestState } from '~/state/atoms/chatAtoms';

export interface DicomImportBinding {
    readonly open: boolean;
    readonly selectedPatientId?: string;
    /** The dialog's own patient selection (also moves to the series step). */
    selectPatient(patient: DICOMPatientPresentation): void;
    isSeriesSelected(patientId: string, seriesInstanceUid: string): boolean;
    toggleSeries(
        patientId: string,
        studyInstanceUid: string,
        seriesInstanceUid: string
    ): void;
}

export function useAgentDicomImportRequest(dialog: DicomImportBinding): void {
    const [request, setRequest] = useRecoilState(agentDicomImportRequestState);
    const {
        open,
        selectedPatientId,
        selectPatient,
        isSeriesSelected,
        toggleSeries,
    } = dialog;

    useEffect(() => {
        if (!request || !open) {
            return;
        }
        if (selectedPatientId !== request.patientId) {
            // Selecting the patient clears any ticked series and sets the
            // target; the series are ticked on the next render.
            selectPatient({
                patientID: request.patientId,
                patientName: request.patientName,
                target_names: [request.targetName],
            });
            return;
        }
        for (const uid of request.seriesInstanceUids) {
            if (!isSeriesSelected(request.patientId, uid)) {
                toggleSeries(request.patientId, request.studyInstanceUid, uid);
            }
        }
        setRequest(null);
    }, [
        request,
        open,
        selectedPatientId,
        selectPatient,
        isSeriesSelected,
        toggleSeries,
        setRequest,
    ]);
}
